import { Component, OnInit, ChangeDetectionStrategy, Input, OnDestroy, ViewChild } from '@angular/core';
import { BackgroundModel } from 'src/app/engine/models/background.model';
import { Subject, Observable } from 'rxjs';
import { Select, Store, Actions, ofActionCompleted } from '@ngxs/store';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { takeUntil, filter } from 'rxjs/operators';
import { SetSelectedChapterBackground, SetAllChaptersBackground, ResetSelectedChapterBackground } from 'src/app/story/state/chapters/story.chapters.actions';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ColorService } from 'src/app/shared/color.service';
import { TabComponent } from 'src/app/shared/components/tabs/tab/tab.component';
import { environment } from 'src/environments/environment';
import { plainToClass } from 'class-transformer';

import * as _ from 'lodash';
import { MatSnackBar } from '@angular/material';
import { loftSwatch, studioSwatch, transparentBackground } from './background-config';

@Component({
  selector: 'app-background-color-dialog',
  templateUrl: './background-color-dialog.component.html',
  styleUrls: ['./background-color-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BackgroundColorDialogComponent implements OnInit, OnDestroy {

  @Select(StoryChaptersState.getSelectedChapterBackground)
  background$: Observable<BackgroundModel>;

  @Select(StoryChaptersState.getAllChaptersBackground)
  allChapterBackgrounds$: Observable<BackgroundModel[]>;

  @Select(StoryChaptersState.getSelectedChapterIndex)
  selectedChapterIndex$: Observable<number>;

  @ViewChild('presetTab')
  presetTab: TabComponent;

  @ViewChild('customTab')
  customTab: TabComponent;

  // selected preset
  presetBackground: BackgroundModel;

  // selected preset
  distinctBackgrounds: BackgroundModel[];

  // environment based preset swatch
  presetBackgrounds = environment.exportType === 'module'
    ? plainToClass(BackgroundModel, loftSwatch) // loft
    : plainToClass(BackgroundModel, studioSwatch); // studio

  form: FormGroup;
  ngUnsubscribe = new Subject();

  constructor(
    private store: Store,
    private actions: Actions,
    private fb: FormBuilder,
    private colorService: ColorService,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit() {
    this.initPresets();
    this.initForm();

    // messaging
    this.actions.pipe(
      ofActionCompleted(SetAllChaptersBackground),
      takeUntil(this.ngUnsubscribe)
    )
      .subscribe(() => {
        this.snackBar.open('Successfully applied color to all chapters.');
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  initPresets() {
    // track selected preset
    this.presetBackground = this.store.selectSnapshot(StoryChaptersState.getSelectedChapterBackground);
    this.background$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((background) => {
        this.presetBackground = background;
      });

    // track distinct chapter backgrounds
    this.allChapterBackgrounds$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((backgrounds) => {
        this.distinctBackgrounds = backgrounds.filter((background, index) => {
          return backgrounds.findIndex((searchBackgound) => this.backgroundsEqual(background, searchBackgound)) === index;
        });
      });
  }

  initForm() {
    // get current chapter defaults
    const chapterBackground = this.store.selectSnapshot(StoryChaptersState.getSelectedChapterBackground);
    
    // setup form
    this.form = this.fb.group({
      type: ['solid', [Validators.required]],
      color1: ['', [Validators.required]],
      stop1: [0, [Validators.required, Validators.min(0), Validators.max(100)]],
      color2: ['', [Validators.required]],
      stop2: [100, [Validators.required, Validators.min(0), Validators.max(100)]]
    });

    // setup events: set custom background
    this.form.valueChanges
      .pipe(
        filter(() => this.customTab.isActive),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.applyFromCustom();
      });

    // set initial form values
    if (chapterBackground) {
      this.setCustomColorForm(chapterBackground);
    }
  }

  handleFormColorSelected(fieldName: string, color: string) {
    this.form.get(fieldName)
      .setValue(color);
  }

  handlePresetSelected(background: BackgroundModel) {
    this.presetBackground = background;
    this.store.dispatch(new SetSelectedChapterBackground(background));
  }

  handleResetClick() {
    this.store.dispatch(new ResetSelectedChapterBackground());
  }

  handleTabChange(tab: string | number) {
    // apply custom preset
    if (this.customTab.isActive && this.presetBackground) {
      this.setCustomColorForm(this.presetBackground);
    }
  }

  backgroundsEqual(backgroundA: BackgroundModel, backgroundB: BackgroundModel): boolean {
    return _.isEqual(backgroundA, backgroundB);
  }

  getBackgroundHex(background: BackgroundModel): string {
    const colors = background.colors;
    return this.colorService.rgbToHex([colors[0], colors[1], colors[2]]);
  }

  getFormBackgroundHex(fieldName: string): string {
    return this.form.get(fieldName).value;
  }

  applyAll() {
    const type = this.form.value.type;
    // determine mode
    if (this.presetTab.isActive) {
      this.store.dispatch(new SetAllChaptersBackground(this.presetBackground));
    } else if (this.customTab.isActive) {
      const customBackground = this.formToBackgroundColor(type);
      if (customBackground) {
        this.store.dispatch(new SetAllChaptersBackground(customBackground));
      }
    }
  }

  applyFromCustom() {
    const type = this.form.value.type;
    const customBackground = this.formToBackgroundColor(type);
    if (customBackground) {
      this.store.dispatch(new SetSelectedChapterBackground(customBackground));
    }
  }

  private setCustomColorForm(background: BackgroundModel) {    
    let type = 'solid';
    let color1 = '';
    let color2 = '';
    let stop1 = 0;
    let stop2 = 100;

    const { colors, gradientType, colorStops } = background;
    // set colors
    const firstRGB: [number, number, number] = [
      colors[0],
      colors[1],
      colors[2]
    ];
    const secondRGB: [number, number, number] = colors.length < 8
      ? firstRGB
      : [
        colors[4],
        colors[5],
        colors[6]
      ];
    color1 = this.colorService.rgbToHex(firstRGB, false);
    color2 = this.colorService.rgbToHex(secondRGB, false);

    // set type: handle transparent
    if (BackgroundModel.isTransparent(background)) {
      type = 'transparent';
    } else if (_.isEqual(firstRGB, secondRGB)) {
      type = 'solid';
    } else {
      type = gradientType;
    }

    // set stops
    stop1 = colorStops[0];
    stop2 = colorStops[1];

    this.form.patchValue({
      type: type,
      color1: color1,
      stop1: stop1,
      color2: color2,
      stop2: stop2
    });
  }

  private formToBackgroundColor(type: string): BackgroundModel {
    const { color1, color2, stop1, stop2 } = this.form.value;
    const formGroup = this.form;

    // special case
    if (type === 'transparent') {
      return transparentBackground;
    }

    // validation
    if (!formGroup.valid) {
      return null;
    }
    
    let background: BackgroundModel;
    const colors: number[] = [];
    const gradientType = type === 'radial' ? 'radial' : 'linear';

    // set color 1
    if (color1) {
      colors.push(...this.colorService.hexToRgb(color1, false), 1);
    }
    // set color 2
    if (color2 && type !== 'solid') {
      colors.push(...this.colorService.hexToRgb(color2, false), 1);
    } else { // repeat color
      colors.push(...colors);
    }

    background = new BackgroundModel(
      colors,
      gradientType,
      [stop1, stop2]
    );
    return background;
  }
}
